import { useState, useEffect, useRef } from "react";
import { api } from "../api/client";
import { I } from "./Icons";
import { clsx, OWNER_LABEL } from "./Primitives";
import type { Partner, AskResult, AskChange, NewDealDraft } from "../types";

const AI_EXAMPLES = [
  "Mark 'Svare Scanmudring' as done",
  "Push the Perenco next step to 12 June",
  "Move the Nigeria two-field deal to structuring, 55%",
  "Add a task for Trond: book Höegh meeting Tuesday",
  "Add a note to the Mozambique LNG deal: Höegh wants the local-content angle",
  "Add a deal: Impact in South Africa, Fishbones stimulation, substantial potential",
];

interface Props {
  open: boolean;
  prefill: string;
  close: () => void;
  bump: () => void;
  partners: Record<string, Partner>;
  onNewDeal?: (draft: NewDealDraft) => void;
}

function describe(ch: AskChange): string {
  if (ch.type === "task_done") return (ch.value === false ? "Reopen task: " : "Complete task: ") + ch.id;
  if (ch.type === "task_add") return `Add task: "${ch.task}" (${OWNER_LABEL[ch.owner] || ch.owner || "Geir"})`;
  if (ch.type === "deal_update") {
    const parts = Object.entries(ch.fields).map(([k, v]) => `${k} → ${v}`);
    return `Update ${ch.id}: ${parts.join(", ")}`;
  }
  if (ch.type === "deal_note") return `Note on ${ch.id}: "${ch.note}"`;
  if (ch.type === "email_allocate") return `Route email ${ch.id} → ${ch.dealId}`;
  return (ch as { type: string }).type;
}

export default function AskSaga({ open, prefill, close, bump, onNewDeal }: Props) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AskResult | null>(null);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setText(prefill || "");
      setResult(null);
      setApplied(false);
      setError(null);
      setTimeout(() => ref.current?.focus(), 60);
    }
  }, [open, prefill]);

  const run = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setResult(null);
    setApplied(false);
    setError(null);
    try {
      const out = await api.post<AskResult>("/api/ai/ask", { instruction: text.trim() });
      out.changes = Array.isArray(out.changes) ? out.changes : [];
      // A new deal opens the deal form, pre-filled, to check and save.
      if (out.newDeal && onNewDeal && out.changes.length === 0) {
        close();
        onNewDeal(out.newDeal);
        setBusy(false);
        return;
      }
      setResult(out);
    } catch {
      setError("Saga AI couldn't parse that. Try rephrasing, e.g. \"mark Svare Scanmudring done\".");
    }
    setBusy(false);
  };

  const [applying, setApplying] = useState(false);
  const apply = async () => {
    if (!result || applying || applied) return;
    setApplying(true);
    try {
      await api.post("/api/ai/ask/apply", { changes: result.changes });
      bump();
      setApplied(true);
      setTimeout(() => close(), 850);
    } catch {
      setError("Failed to apply changes.");
    }
    setApplying(false);
  };

  if (!open) return null;

  return (
    <div className="ai-overlay" onMouseDown={close}>
      <div className="ai-panel" onMouseDown={e => e.stopPropagation()}>
        <div className="ai-head">
          <span className="ai-spark">{I.ai({ size: 17 })}</span>
          <div>
            <div className="ai-title">Ask Saga</div>
            <div className="ai-sub">Type in plain language to update deals, tasks and routing</div>
          </div>
          <span className="cmd-esc">esc</span>
        </div>

        <div className="ai-input-wrap">
          <textarea ref={ref} className="ai-input" rows={2} value={text}
            placeholder="e.g. push the Perenco next step to 12 June and mark Svare Scanmudring done"
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); run(); } }} />
          <button className="btn primary ai-run" onClick={run} disabled={busy || !text.trim()}>
            {busy ? "Thinking…" : <>{I.send({ size: 15 })} Run</>}
          </button>
        </div>

        {!result && !busy && (
          <div className="ai-examples">
            {AI_EXAMPLES.map(ex => (
              <button key={ex} className="ai-ex" onClick={() => setText(ex)}>{ex}</button>
            ))}
          </div>
        )}

        {error && <div className="ai-error">{error}</div>}

        {result && (
          <div className="ai-result">
            {result.reply && <div className="ai-reply">{result.reply}</div>}
            {result.changes.length > 0 && (
              <>
                <div className="ai-changes-label">{applied ? "Applied" : "Proposed changes"}</div>
                <div className="ai-changes">
                  {result.changes.map((ch, i) => (
                    <div key={i} className={clsx("ai-change", applied && "done")}>
                      {I.check({ size: 14, w: 2.2, style: { color: applied ? "var(--positive)" : "var(--faint)", flex: "none" } })}
                      <span>{describe(ch)}</span>
                    </div>
                  ))}
                </div>
                {!applied && (
                  <div className="ai-actions">
                    <button className="btn primary sm" onClick={apply} disabled={applying}>
                      {I.check({ size: 14, w: 2.2 })} {applying ? "Applying…" : `Apply ${result.changes.length} change${result.changes.length > 1 ? "s" : ""}`}
                    </button>
                    <button className="btn ghost sm" onClick={() => setResult(null)}>Discard</button>
                  </div>
                )}
              </>
            )}
            {result.changes.length === 0 && !result.reply && <div className="ai-reply">No changes needed.</div>}
          </div>
        )}

        <div className="ai-foot">
          <span><kbd>⌘</kbd><kbd>↵</kbd> run</span>
          <span className="ai-foot-note">Powered by Grok · you confirm every change</span>
        </div>
      </div>
    </div>
  );
}
